import React, { useEffect, useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { api } from '../../lib/api'
import { KpiCard, Section, CredentialingBadge } from '../../components/ui'
import { StatusBanner } from '../../components/StatusBanner'
import { Timeline } from '../../components/Timeline'
import { NotesPanel } from '../../components/NotesPanel'
import { RelatedEntityList } from '../../components/RelatedEntityList'
import type { AdminProviderDetail as AdminProviderDetailType } from '../../lib/types'

const SESSION_EVENT_TYPES = ['session_attended', 'session_cancelled', 'session_no_show', 'session_rescheduled']

export default function AdminProviderDetail() {
  const { providerId } = useParams<{ providerId: string }>()
  const [provider, setProvider] = useState<AdminProviderDetailType | null>(null)
  const [verifying, setVerifying] = useState(false)

  async function load() {
    setProvider(await api(`/admin/providers/${providerId}`))
  }

  useEffect(() => { load() }, [providerId])

  if (!provider || !providerId) return <div className="page-loading">Loading provider…</div>

  const { risk } = provider

  async function verifyEmailManually() {
    setVerifying(true)
    try {
      await api(`/admin/providers/${providerId}/verify-email`, { method: 'POST' })
      await load()
    } finally {
      setVerifying(false)
    }
  }

  return (
    <div>
      <Link to="/admin/providers" className="muted" style={{ fontSize: 13 }}>&larr; All providers</Link>
      <div className="page-head">
        <h1>{provider.name}</h1>
        <div className="ph-sub">
          {provider.credentials ? `${provider.credentials} · ` : ''}
          {provider.organization_name ? (
            <Link to={`/admin/organizations/${provider.organization_id}`}>{provider.organization_name}</Link>
          ) : '—'}
        </div>
      </div>

      {/* --- Risk --- */}
      <Section title="Risk">
        <StatusBanner
          riskLevel={risk.risk_level}
          score={risk.risk_score}
          components={[
            { label: 'Total cases', value: risk.components.total_cases },
            { label: 'Active cases', value: risk.components.active_cases },
            { label: 'Early dropout count', value: risk.components.early_dropout_count },
            { label: 'Dropout rate', value: `${Math.round(risk.components.dropout_rate * 100)}%` },
            { label: 'Open high/critical alerts', value: risk.components.open_critical_or_high_alerts },
            { label: 'Fully onboarded', value: risk.components.fully_onboarded ? 'Yes' : 'No' },
          ]}
        />
      </Section>

      <div className="section-block">
        <div className="section-head"><h2>Profile</h2></div>
        <div className="kpis">
          <KpiCard label="Status" value={provider.status} />
          <KpiCard label="Account status" value={provider.account_status} />
          <KpiCard label="Seat status" value={provider.seat_status} />
          <KpiCard label="Capacity" value={provider.capacity_status} />
          <KpiCard label="Accepting new clients" value={provider.accepting_new_clients ? 'Yes' : 'No'} />
          <KpiCard label="Profile completed" value={provider.profile_completed_at ? new Date(provider.profile_completed_at).toLocaleDateString() : 'Not yet'} />
          <KpiCard label="Email verified" value={provider.email_verified == null ? 'No login' : provider.email_verified ? 'Yes' : 'No'} />
        </div>
        {provider.email_verified === false && (
          <div style={{ marginTop: 10 }}>
            <button className="btn small outline" disabled={verifying} onClick={verifyEmailManually}>
              {verifying ? 'Verifying…' : 'Verify email manually'}
            </button>
            <p className="muted" style={{ fontSize: 12, marginTop: 4 }}>
              Use this if the provider never received or can't click their verification email — email verification is required for them to appear in patient matching.
            </p>
          </div>
        )}
      </div>

      {/* --- Timeline --- */}
      <Section title="Timeline">
        <Timeline providerId={providerId} limit={25} />
        <Link to="/admin/event-logs" className="muted" style={{ fontSize: 13 }}>View full event log &rarr;</Link>
      </Section>

      {/* --- Tasks & Notes --- */}
      <NotesPanel attachedToType="provider" attachedToId={providerId} organizationId={provider.organization_id} />

      {/* --- Assigned patients --- */}
      <Section title="Assigned patients" note={`${provider.assigned_patients.length}`}>
        <RelatedEntityList
          items={provider.assigned_patients.map((p) => ({
            id: p.id, to: `/admin/patients/${p.id}`,
            primary: p.name || 'Unnamed patient',
            secondary: `${p.case_status} · ${p.continuity_milestone.replace(/_/g, ' ')}`,
          }))}
          emptyLabel="No patients assigned yet."
        />
      </Section>

      {/* --- Credentialing --- */}
      <Section title="Credentialing">
        <div className="card">
          <div className="row" style={{ marginBottom: 10 }}>
            <span>Status</span>
            <CredentialingBadge status={provider.credentialing_status} />
          </div>
          <div className="kpis">
            <KpiCard label="NPI" value={provider.npi_number || '—'} />
            <KpiCard label="License" value={provider.license_number ? `${provider.license_number} (${provider.license_state || '—'})` : '—'} />
            <KpiCard label="Verified" value={provider.verified_at ? new Date(provider.verified_at).toLocaleDateString() : 'Not yet'} />
          </div>
          {provider.verification_notes && (
            <p className="muted" style={{ fontSize: 13, marginTop: 8 }}>{provider.verification_notes}</p>
          )}
        </div>
      </Section>

      {/* --- Events (session-level history) --- */}
      <Section title="Events" note="session activity across all cases">
        <Timeline providerId={providerId} limit={50} filterTypes={SESSION_EVENT_TYPES} emptyLabel="No session events recorded yet." />
      </Section>
    </div>
  )
}
